<template>
  <div id="app">
    <div
      v-if="showInsecureZones"
      class="fixed w-full h-full top-0 left-0 flex items-center justify-center text-center z-50"
    >
      <div class="absolute w-full h-full bg-gray-900 opacity-50"></div>

      <div
        class="p-2 bg-white w-11/12 md:max-w-md mx-auto rounded shadow-lg z-50 overflow-y-auto"
        @click="showInsecureZones = false"
      >
        <h2 class="text-2xl mb-3">Insecure Zones</h2>
        <div v-for="zone in state.insecureZones" v-bind:key="zone">
          <p>{{ zone }}</p>
        </div>
        <button class="btn btn-blue mt-4" @click="showInsecureZones = false">
          Close
        </button>
      </div>
    </div>

    <div
      v-if="connectionError"
      class="fixed w-full h-full top-0 left-0 flex items-center justify-center text-center z-50"
    >
      <div class="absolute w-full h-full bg-gray-900 opacity-50"></div>

      <div
        class="p-2 bg-white w-11/12 md:max-w-md mx-auto rounded z-50 overflow-y-auto"
      >
        <h2 class="text-red-600 text-2xl font-bold capitalize">
          Communication Error
        </h2>
        <br />
        <h2 class="text-red-600 text-2xl font-bold capitalize">
          Reconnecting...
        </h2>
      </div>
    </div>

    <div class="container mx-auto flex justify-center text-center">
      <div>
        <div class="relative">
          <p class="text-4xl capitalize" :class="stateClass">
            {{ state.state }}
          </p>
          <div
            v-if="state.insecureZones && state.insecureZones.length > 0"
            class="absolute top-0 right-0 pt-1 pr-1"
            @click="showInsecureZones = true"
          >
            <i
              class="mdi mdi-alert-circle-outline text-orange-500 text-3xl"
            ></i>
          </div>
        </div>
        <p class="text-2xl">
          {{ state.armedGroup ? state.armedGroup : "\xa0" }}
        </p>

        <div v-if="!error" class="text-2xl">
          <div v-if="displayedCode" class="fg-red-600" label="Code" readonly>
            {{ displayedCode }}
          </div>
          <div v-else class="fg-red-600" label="Code" readonly>
            {{ "\xa0" }}
          </div>
        </div>
        <h2 v-else class="text-red-600 text-2xl font-bold capitalize">
          {{ error }}
        </h2>

        <div class="flex">
          <button
            class="btn btn-orange m-1 w-20 h-20 text-3xl"
            :disabled="!enabledActions.armDefault"
            @click.prevent="handleModeClick('arm', '')"
            @touchstart.prevent="
              !!enabledActions.armDefault && handleModeClick('arm', '')
            "
          >
            <i class="mdi mdi-shield-outline"></i>
          </button>

          <div class="relative">
            <div class="flex">
              <button
                :disabled="enabledActions.armable.length == 0"
                v-click-outside="() => (groupDropdownActive = false)"
                @click="groupDropdownActive = !groupDropdownActive"
                class="btn btn-orange m-1 w-20 h-20 text-3xl"
              >
                <i class="mdi mdi-menu"></i>
              </button>
            </div>
            <div
              v-if="groupDropdownActive"
              class="absolute z-10 w-full mt-2 bg-white rounded-lg border shadow-md py-2"
            >
              <ul>
                <li
                  class="block px-4 py-2 hover:bg-blue-500 hover:text-white cursor-pointer"
                  v-for="group in enabledActions.armable"
                  v-bind:key="group"
                  @click.prevent="handleModeClick('arm', group)"
                  @touchstart.prevent="handleModeClick('arm', group)"
                >
                  {{ group }}
                </li>
              </ul>
            </div>
          </div>
          <button
            class="btn btn-red m-1 w-20 h-20 text-3xl"
            :disabled="!enabledActions.disarm"
            @click.prevent="handleModeClick('disarm')"
            @touchstart.prevent="
              !!enabledActions.disarm && handleModeClick('disarm')
            "
          >
            <i class="mdi mdi-power-standby"></i>
          </button>
        </div>

        <hr class="my-1" />
        <div class="flex">
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('1')"
            @touchstart.prevent="handleDigitClick('1')"
          >
            1
          </button>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('2')"
            @touchstart.prevent="handleDigitClick('2')"
          >
            2
          </button>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('3')"
            @touchstart.prevent="handleDigitClick('3')"
          >
            3
          </button>
        </div>
        <div class="flex">
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('4')"
            @touchstart.prevent="handleDigitClick('4')"
          >
            4
          </button>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('5')"
            @touchstart.prevent="handleDigitClick('5')"
          >
            5
          </button>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('6')"
            @touchstart.prevent="handleDigitClick('6')"
          >
            6
          </button>
        </div>
        <div class="flex">
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('7')"
            @touchstart.prevent="handleDigitClick('7')"
          >
            7
          </button>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('8')"
            @touchstart.prevent="handleDigitClick('8')"
          >
            8
          </button>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @mousedown.prevent="handleDigitClick('9')"
            @touchstart.prevent="handleDigitClick('9')"
          >
            9
          </button>
        </div>
        <div class="flex">
          <div class="m-1 w-20 h-20 text-3xl"></div>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @click.prevent="handleDigitClick('0')"
            @touchstart.prevent="handleDigitClick('0')"
          >
            0
          </button>
          <button
            class="btn btn-black m-1 w-20 h-20 text-3xl"
            @click.prevent="handleDigitClick('CLEAR')"
            @touchstart.prevent="handleDigitClick('CLEAR')"
          >
            <i class="mdi mdi-close-circle-outline"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as vClickOutside from "v-click-outside-x";
import ReconnectingWebSocket from "reconnecting-websocket";

export default {
  name: "App",
  directives: {
    clickOutside: vClickOutside.directive
  },
  components: {},
  data() {
    return {
      groupDropdownActive: false,
      showInsecureZones: false,

      code: "",
      displayedCode: "",
      state: null,

      connectionError: false,
      error: null,
      errorTimer: null
    };
  },
  computed: {
    enabledActions: function() {
      let state = this.state;
      let enabledActions = {};

      if (this.code.length == 0) {
        enabledActions = {
          armDefault: false,
          armable: [],
          disarm: false
        };
        return enabledActions;
      }

      enabledActions.armDefault =
        state.state == "disarmed" &&
        state.armableGroups.includes(state.defaultGroup);
      enabledActions.armable = [];
      if (state.state == "disarmed") {
        state.armableGroups.map(group => {
          enabledActions.armable.push(group);
        });
      }

      enabledActions.disarm = state.state != "disarmed";

      return enabledActions;
    },
    stateClass: function() {
      let state = this.state;

      if (state == "armed") return "setuperror";
      if (state == "armed") return "setuperror";
      return "";
    }
  },
  methods: {
    resetCode() {
      this.code = "";
      this.displayedCode = "";
    },

    handleDigitClick(id) {
      clearTimeout(this.errorTimer);
      this.error = "";

      console.log("ontouchstart");
      var that = this;
      console.log("Click: " + id);

      if (id == "CLEAR") {
        if (this.code != "") {
          this.code = "";
        }
      } else {
        if (this.code == "") {
          this.code = id;
        } else {
          this.code = this.code + id;
        }
      }

      this.displayedCode = this.code.replace(/./g, "*");

      clearTimeout(this.digitTimer);
      this.digitTimer = setTimeout(function() {
        that.resetCode();
      }, 30000);
    },

    handleModeClick(action, group) {
      console.log("Click: " + action);
      var that = this;

      this.groupDropdownActive = false;

      switch (action) {
        case "arm":
          if (group == "") {
            group = this.state.defaultGroup;
          }
          break;
        case "disarm":
          break;
        default:
          return;
      }

      fetch(this.apiUrl + "/" + action, {
        method: "POST",
        body: JSON.stringify({
          code: this.code,
          group: group
        })
      })
        .then(function(response) {
          // handle success
          clearTimeout(that.digitTimer);
          that.resetCode();
          console.log(response);
          return response.text();
        })
        .then(function(text) {
          console.log(text);
          that.error = text;
          clearTimeout(that.errorTimer);
          that.errorTimer = setTimeout(function() {
            that.error = "";
          }, 5000);
        })
        .catch(function(error) {
          // handle error
          console.log(error);
          clearTimeout(that.errorTimer);
          that.connectionError = true;
        });
    },

    beep() {
      var snd = new Audio(
        "data:audio/wav;base64,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"
      );
      snd.play();
    }
  },
  created: function() {
    var that = this;
    this.state = { state: "unknown", insecureZones: [] };

    this.apiUrl = new URL(window.CONFIG.apiBaseUrl, window.location);
    console.log(this.apiUrl);

    var wsUrl = new URL(this.apiUrl);

    wsUrl.protocol = wsUrl.protocol == "https:" ? "wss:" : "ws:";

    var config = {
      debug: true,
      maxReconnectionDelay: 1000,
      minReconnectionDelay: 1000
    };
    this.ws = new ReconnectingWebSocket(wsUrl + "/ws", [], config);
    this.ws.onopen = function() {
      console.log("ping");
      that.ws.send("ping");
      that.pingTimeout = setTimeout(function() {
        console.log("ping timeout");
        that.ws.reconnect();
      }, 5000);
    };
    this.ws.onmessage = function(event) {
      if (event.data == "pong") {
        console.log("pong");
        that.connectionError = null;
        clearTimeout(that.pingTimeout);

        that.pinger = setTimeout(function() {
          console.log("ping");
          that.ws.send("ping");
          that.pingTimeout = setTimeout(function() {
            console.log("ping timeout");
            that.ws.reconnect();
            that.connectionError = true;
          }, 5000);
        }, 5000);

        return;
      }
      console.log(event.data);

      that.state = JSON.parse(event.data);
      clearTimeout(that.errorTimer);
      that.connectionError = null;
      that.error = "";

      if (that.state.state != "triggering") {
        clearInterval(that.beepInterval);
        that.beepInterval = null;
      }
      if (that.state.state == "triggering" && that.beepInterval == null) {
        that.beepInterval = window.setInterval(that.beep, 400);
      }
    };
    this.ws.onerror = function(event) {
      console.error("WebSocket error observed:", event);
      clearTimeout(that.errorTimer);
      clearTimeout(that.pingTimeout);
      that.connectionError = true;
    };

    window.setInterval(function() {
      console.log("ws reconnect");
      clearTimeout(that.pingTimeout);
      that.ws.reconnect();
    }, 300000);

    this.error = "";
    this.resetCode();
  },
  beforeDestroy() {
    if (this.ws) {
      this.ws.close();
    }
    clearTimeout(this.fetchRunsSchedule);
  }
};
</script>

<style lang="scss"></style>
